import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Document, Page, pdfjs } from "react-pdf";
import { X, Download, Loader2, FileWarning } from "lucide-react";
import { getAuthHeaders } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// pdf.js needs its worker script — loaded from a CDN matching the installed
// pdfjs-dist version so it always stays in sync with react-pdf's bundled copy.
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// Generic in-app PDF viewer — fetches the file as a blob (auth header attached,
// same permission checks as the underlying endpoint) and renders every page
// via pdf.js/react-pdf scaled to the container's real width. This guarantees
// a true fit-to-width on every device (no reliance on the browser's own PDF
// plugin, which behaves inconsistently on mobile) — only vertical scroll
// between pages, never horizontal.
export default function CVPdfViewerModal({ title, subtitle, fetchPath, downloadFilename, onClose }) {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [blobUrl, setBlobUrl] = useState(null);
  const [isPdf, setIsPdf] = useState(true);
  const [numPages, setNumPages] = useState(null);
  const [pdfError, setPdfError] = useState(false);
  const [pageWidth, setPageWidth] = useState(0);
  const containerRef = useRef(null);

  useEffect(() => {
    let url;
    (async () => {
      try {
        const res = await axios.get(`${API}${fetchPath}`, { headers: getAuthHeaders(), responseType: "blob" });
        const contentType = res.headers?.["content-type"] || "application/pdf";
        url = window.URL.createObjectURL(new Blob([res.data], { type: contentType }));
        setIsPdf(contentType.includes("pdf"));
        setBlobUrl(url);
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    })();
    return () => { if (url) window.URL.revokeObjectURL(url); };
  }, [fetchPath]);

  // Measure the real available width of the viewer body and keep it in sync
  // on resize/orientation change — this is what makes every page fit-to-width.
  useEffect(() => {
    if (!containerRef.current) return;
    const el = containerRef.current;
    const update = () => setPageWidth(el.clientWidth);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [blobUrl]);

  const handleDownload = () => {
    if (!blobUrl) return;
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = downloadFilename || "CV.pdf";
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[60] flex items-end sm:items-center justify-center overflow-hidden" data-testid="cv-viewer-modal" onClick={onClose}>
      <div
        className="bg-white w-full h-[92dvh] rounded-t-2xl sm:h-[85vh] sm:max-w-2xl sm:rounded-2xl flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-slate-100 flex-shrink-0">
          <div className="min-w-0">
            <h3 className="font-poppins font-bold text-base text-slate-900 truncate">{title}</h3>
            {subtitle && <p className="text-xs text-slate-500 truncate">{subtitle}</p>}
          </div>
          <button onClick={onClose} data-testid="cv-viewer-close" className="p-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors flex-shrink-0">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div ref={containerRef} className="flex-1 min-h-0 min-w-0 bg-slate-100 relative overflow-y-auto overflow-x-hidden">
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center">
              <Loader2 className="h-7 w-7 text-[#028CF8] animate-spin" />
            </div>
          )}
          {!loading && error && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 px-6 text-center">
              <FileWarning className="h-8 w-8 text-slate-400" />
              <p className="text-sm text-slate-600" data-testid="cv-viewer-error">{t("cvViewer.loadError")}</p>
            </div>
          )}
          {!loading && !error && blobUrl && isPdf && !pdfError && (
            <div className="flex flex-col items-center gap-3 py-3" data-testid="cv-viewer-pdf-pages">
              <Document
                file={blobUrl}
                onLoadSuccess={({ numPages: n }) => setNumPages(n)}
                onLoadError={() => setPdfError(true)}
                loading={
                  <div className="flex items-center justify-center py-16">
                    <Loader2 className="h-6 w-6 text-[#028CF8] animate-spin" />
                  </div>
                }
                error={
                  <div className="flex flex-col items-center gap-2 px-6 py-16 text-center">
                    <FileWarning className="h-8 w-8 text-slate-400" />
                    <p className="text-sm text-slate-600">{t("cvViewer.noPreview")}</p>
                  </div>
                }
              >
                {numPages && pageWidth > 0 && Array.from({ length: numPages }, (_, i) => (
                  <Page
                    key={`page_${i + 1}`}
                    pageNumber={i + 1}
                    width={pageWidth}
                    renderAnnotationLayer={false}
                    className="shadow-sm mb-3 !bg-white"
                  />
                ))}
              </Document>
            </div>
          )}
          {!loading && !error && blobUrl && isPdf && pdfError && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 px-6 text-center">
              <FileWarning className="h-8 w-8 text-slate-400" />
              <p className="text-sm text-slate-600" data-testid="cv-viewer-pdf-error">{t("cvViewer.noPreview")}</p>
            </div>
          )}
          {!loading && !error && blobUrl && !isPdf && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 px-6 text-center">
              <FileWarning className="h-8 w-8 text-slate-400" />
              <p className="text-sm text-slate-600">{t("cvViewer.noPreview")}</p>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 px-5 py-4 border-t border-slate-100 flex-shrink-0" style={{ paddingBottom: "calc(16px + env(safe-area-inset-bottom))" }}>
          <button onClick={onClose} data-testid="cv-viewer-back" className="h-11 px-4 rounded-xl border border-slate-200 text-slate-600 text-sm font-semibold hover:bg-slate-50 transition-colors">
            {t("cvViewer.back")}
          </button>
          <button
            onClick={handleDownload}
            disabled={!blobUrl}
            data-testid="cv-viewer-download"
            className="flex items-center gap-2 h-11 px-5 rounded-xl border-2 border-[#028CF8]/30 text-[#028CF8] text-sm font-semibold hover:bg-[#028CF8]/5 transition-colors disabled:opacity-50"
          >
            <Download className="h-4 w-4" /> {t("cvViewer.downloadPdf")}
          </button>
        </div>
      </div>
    </div>
  );
}
